import React, { useMemo, useEffect, useRef } from 'react';
import get from 'lodash/get';
import { metrics as BM3 } from '@atlassian/browser-metrics';
import { getUserLocation } from '@atlassian/jira-platform-router-utils/src/common/utils/index.tsx';
import { getRenderTraceFromSSRGlobal } from '@atlassian/jira-render-tracer-analytics/src/main.tsx';
import {
	NAVIGATION_TOP_SEGMENT_NAME,
	NAVIGATION_SIDEBAR_SEGMENT_NAME,
	NAVIGATION_SSR_MARK_PREFIX,
	NAVIGATION_TOP_MARKS,
	NAVIGATION_SIDEBAR_MARKS,
} from './constants.tsx';

type PageSegmentKey = typeof NAVIGATION_TOP_SEGMENT_NAME | typeof NAVIGATION_SIDEBAR_SEGMENT_NAME;

type NavigationPerformanceMarkProps = {
	metricKey: string;
	segmentKey?: PageSegmentKey;
};

type NavigationPerformanceReportProps = {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	customData?: Record<any, any>;
	segmentKey?: PageSegmentKey;
};

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export {
	NAVIGATION_TOP_SEGMENT_NAME,
	NAVIGATION_SIDEBAR_SEGMENT_NAME,
	NAVIGATION_TOP_MARKS,
	NAVIGATION_SIDEBAR_MARKS,
} from './constants';

const createTopNavPageSegment = () =>
	BM3.pageSegmentLoad({
		key: NAVIGATION_TOP_SEGMENT_NAME,
		timings: [
			{
				key: 'ttic',
				startMark: NAVIGATION_TOP_MARKS.renderStart,
				endMark: NAVIGATION_TOP_MARKS.renderEnd,
			},
			{
				key: 'tti',
				endMark: NAVIGATION_TOP_MARKS.renderEnd,
			},
			{
				key: 'ttr',
				endMark: NAVIGATION_TOP_MARKS.renderSsrEnd,
			},
			{
				key: 'ttrc',
				startMark: NAVIGATION_TOP_MARKS.renderSsrStart,
				endMark: NAVIGATION_TOP_MARKS.renderSsrEnd,
			},
			{
				key: 'bundle-load',
				startMark: NAVIGATION_TOP_MARKS.bundleLoadStart,
				endMark: NAVIGATION_TOP_MARKS.renderStart,
			},
		],
		histogram: {
			initial: {
				fmp: '2500_3000_3500_4000_5000_6000',
				tti: '4000_4500_5000_6000_7000_8000_9000',
			},
			transition: {
				fmp: '2500_3000_3500_4000_5000_6000',
				tti: '2500_3000_3500_4000_5000_6000',
			},
		},
	});

// only adding performance marks for the ADDONS (aka APPS) dropdown to unblock growth project
// will add other dropdown metrics in BLU-853
export const NAVIGATION_TOP_DROPDOWN_MARKS = {
	ADDONS: BM3.interaction({
		key: 'navigation-top.addons-dropdown-opened',
	}),
} as const;

const createSidebarPageSegment = () =>
	BM3.pageSegmentLoad({
		key: NAVIGATION_SIDEBAR_SEGMENT_NAME,
		timings: [
			{
				key: 'renderAttempt',
				startMark: NAVIGATION_SIDEBAR_MARKS.renderAttempt,
				endMark: NAVIGATION_SIDEBAR_MARKS.renderAttempt,
			},
			{
				key: 'ttic',
				startMark: NAVIGATION_SIDEBAR_MARKS.renderStart,
				endMark: NAVIGATION_SIDEBAR_MARKS.renderEnd,
			},
			{
				key: 'tti',
				endMark: NAVIGATION_SIDEBAR_MARKS.renderEnd,
			},
			{
				key: 'ttr',
				endMark: NAVIGATION_SIDEBAR_MARKS.renderSsrEnd,
			},
			{
				key: 'ttrc',
				startMark: NAVIGATION_SIDEBAR_MARKS.renderSsrStart,
				endMark: NAVIGATION_SIDEBAR_MARKS.renderSsrEnd,
			},
			{
				key: 'load',
				startMark: NAVIGATION_SIDEBAR_MARKS.loadStart,
				endMark: NAVIGATION_SIDEBAR_MARKS.loadEnd,
			},
		],
		histogram: {
			initial: {
				fmp: '2500_3000_3500_4000_5000_6000',
				tti: '4000_4500_5000_6000_7000_8000_9000',
			},
			transition: {
				fmp: '2500_3000_3500_4000_5000_6000',
				tti: '2500_3000_3500_4000_5000_6000',
			},
		},
	});

export const pageSegmentStore = (() => {
	const segmentConstructors = {
		[NAVIGATION_TOP_SEGMENT_NAME]: createTopNavPageSegment,
		[NAVIGATION_SIDEBAR_SEGMENT_NAME]: createSidebarPageSegment,
	};
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const segments: Record<string, any> = {};

	return {
		getSegment: (key: PageSegmentKey) => {
			if (!segments[key]) {
				if (!segmentConstructors[key]) {
					throw new Error(`Segment constructor for ${key} is not defined.`);
				}

				segments[key] = segmentConstructors[key]();
			}

			return segments[key];
		},
	};
})();

export const useNavPerformanceMetrics = (
	segmentKey: PageSegmentKey = NAVIGATION_TOP_SEGMENT_NAME,
) => {
	const startedRef = useRef(false);
	const segment = useRef(pageSegmentStore.getSegment(segmentKey)).current;

	if (!__SERVER__ && !startedRef.current) {
		segment && segment.startFromPageLoad();
		startedRef.current = true;
	}

	return useMemo(
		() =>
			// object assign here to extend origianl segment object and save prototypes
			Object.assign(segment, {
				markSSR: (metricKey: string) => {
					const tmpSsrMetricKey = `${NAVIGATION_SSR_MARK_PREFIX}.${metricKey}`;

					// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
					if (window.SPA_STATE) {
						// @ts-expect-error - TS2339 - Property 'startTime' does not exist on type 'unknown'. | TS2339 - Property 'duration' does not exist on type 'unknown'.
						const { startTime = 0, duration = 0 } =
							get(getRenderTraceFromSSRGlobal(), ['traces', `render/${tmpSsrMetricKey}`]) || {};
						const markTime = startTime + duration;

						markTime && markTime > 0 && segment.mark(metricKey, markTime);
					}
				},
			}),
		[segment],
	);
};

const NavigationPerformanceMark = ({ segmentKey, metricKey }: NavigationPerformanceMarkProps) => {
	const navSegment = useNavPerformanceMetrics(segmentKey);

	useEffect(
		() => navSegment.mark(metricKey),

		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);

	return null;
};

const NavigationPerformanceReport = ({
	segmentKey,
	customData,
}: NavigationPerformanceReportProps) => {
	const navSegment = useNavPerformanceMetrics(segmentKey);

	useEffect(() => {
		navSegment.stop({
			customData: { ...customData, userLocation: getUserLocation() },
		});
		navSegment.clear();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return null;
};

export const NavTopPerformanceMark = (props: NavigationPerformanceMarkProps): React.JSX.Element => (
	<NavigationPerformanceMark {...props} segmentKey={NAVIGATION_TOP_SEGMENT_NAME} />
);

export const NavTopPerformanceReport = (
	props: NavigationPerformanceReportProps,
): React.JSX.Element => (
	<NavigationPerformanceReport {...props} segmentKey={NAVIGATION_TOP_SEGMENT_NAME} />
);

export const NavSidebarPerformanceMark = (
	props: NavigationPerformanceMarkProps,
): React.JSX.Element => (
	<NavigationPerformanceMark {...props} segmentKey={NAVIGATION_SIDEBAR_SEGMENT_NAME} />
);

export const NavSidebarPerformanceReport = (
	props: NavigationPerformanceReportProps,
): React.JSX.Element => (
	<NavigationPerformanceReport {...props} segmentKey={NAVIGATION_SIDEBAR_SEGMENT_NAME} />
);
